/**
 * Hex-Koordinaten → Bildschirmkoordinaten. **Die einzige Stelle, an der das passiert.**
 *
 * `CLAUDE.md` Regel 9 trennt logische und visuelle Boardstruktur: die Engine rechnet in
 * axialen Hex-Koordinaten und darf nie von der Darstellung abhängen. Diese Umrechnung gehört
 * deshalb hierher und **nicht** in die Engine — eine Designänderung darf die Geometrie nicht
 * berühren, und eine Geometrieänderung nicht das Design.
 *
 * **Die Form ist gegen `docs/board-reference.svg` geprüft, Feld für Feld** (`geometry.test.ts`).
 * Die Grafik ist vom User für Geometrie und Farben als verbindlich erklärt; von vier plausiblen
 * Varianten (pointy-top, flat-top, invertierte y-Achse, getauschte Achsen) passt genau eine, und
 * drei davon sehen auf einem Bildschirm wie ein ordentliches Brett aus — nur gedreht oder
 * gespiegelt gegenüber der Vorlage.
 *
 * **Pointy-top**, also mit Spitzen oben und unten. Das ist keine Geschmacksfrage: die
 * Referenzgrafik ist so gebaut, und die Reihenbuchstaben A bis F laufen in dieser Orientierung
 * von außen nach innen (`docs/GAME_RULES.md`, **E14**).
 *
 * **Die absolute Größe ist bedeutungslos**, weil das `viewBox` skaliert. Sie wirkt nur relativ —
 * auf Linienbreiten und Schriftgrößen im Verhältnis zur Feldgröße.
 */

import { boardCells, createBoard, type Hex } from '@flaghex/engine';

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface ViewBox {
  readonly minX: number;
  readonly minY: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Die Feldgröße der Referenzgrafik (Umkreisradius in SVG-Einheiten).
 *
 * Steht hier, damit der Vergleich mit der Vorlage möglich ist — und als Vorgabe, damit
 * Linienbreiten und Schriftgrößen überall im selben Verhältnis stehen.
 */
export const REFERENCE_HEX_SIZE = 46;

const SQRT3 = Math.sqrt(3);

/** Der Mittelpunkt eines Feldes. */
export function hexToPixel(cell: Hex, size: number): Point {
  return {
    x: size * (SQRT3 * cell.q + (SQRT3 / 2) * cell.r),
    y: size * (1.5 * cell.r),
  };
}

/**
 * Die sechs Eckpunkte eines Feldes, **in der Reihenfolge der Referenzgrafik**.
 *
 * Erste Ecke bei 30°, dann in 60°-Schritten. Die Reihenfolge ist Teil des Vertrags: nur so
 * lässt sich ein erzeugtes Brett gegen die Vorlage diffen, statt Mengen vergleichen zu müssen.
 */
export function hexCorners(cell: Hex, size: number): readonly Point[] {
  const center = hexToPixel(cell, size);
  return Array.from({ length: 6 }, (_unused, index) => {
    const angle = ((30 + 60 * index) * Math.PI) / 180;
    return {
      x: center.x + size * Math.cos(angle),
      y: center.y + size * Math.sin(angle),
    };
  });
}

/**
 * Sechs Eckpunkte um einen **gegebenen Mittelpunkt**, mit eigenem Radius.
 *
 * Gebraucht für Formen *innerhalb* eines Feldes: die zweite Kontur des Rings, der Innenring des
 * Zielfeldes, der Schlagring. `hexCorners` taugt dafür nicht — dort bestimmt `size` zugleich die
 * **Lage** des Feldes, ein kleinerer Wert verschöbe also das ganze Brett statt die Form zu
 * verkleinern. Ein Fehler, der auf einem einzelnen Feld noch richtig aussieht.
 */
export function cornersAround(center: Point, radius: number): readonly Point[] {
  return Array.from({ length: 6 }, (_unused, index) => {
    const angle = ((30 + 60 * index) * Math.PI) / 180;
    return {
      x: center.x + radius * Math.cos(angle),
      y: center.y + radius * Math.sin(angle),
    };
  });
}

/**
 * Der Ausschnitt, der alle Felder zeigt.
 *
 * Gerechnet über die **Eckpunkte** und nicht die Mittelpunkte: sonst wären die äußeren Felder
 * halb abgeschnitten. Der Rand ist ein Parameter, weil er zur Darstellung gehört und nicht zur
 * Geometrie — Beschriftungen und Auswahlringe brauchen Platz, aber wie viel, entscheidet das
 * Design.
 *
 * **Ein leeres Brett hat keine Ausdehnung statt einer unendlichen:** `Math.min()` ohne Argumente
 * ist `Infinity`, und ein `viewBox` daraus macht die ganze Seite unlesbar.
 */
export function boardViewBox(
  cells: readonly Hex[],
  size: number,
  padding = 0,
): ViewBox {
  if (cells.length === 0) {
    return { minX: 0, minY: 0, width: 0, height: 0 };
  }
  const corners = cells.flatMap((cell) => hexCorners(cell, size));
  const xs = corners.map((corner) => corner.x);
  const ys = corners.map((corner) => corner.y);
  const minX = Math.min(...xs) - padding;
  const minY = Math.min(...ys) - padding;
  return {
    minX,
    minY,
    width: Math.max(...xs) + padding - minX,
    height: Math.max(...ys) + padding - minY,
  };
}

/**
 * Wie breit der Rand um das Brett ist, relativ zur Feldgröße — Platz für Auswahlränder.
 *
 * Steht hier und nicht in `BoardSvg`, seit auch der Spielbildschirm den Ausgangsausschnitt
 * braucht (für den Zoom). Zwei Ränder wären zwei Ausschnitte, und der Zoom zeigte dann auf etwas
 * anderes als das Brett.
 */
export const BOARD_PADDING_RATIO = 0.25;

/** Der Ausgangsausschnitt eines Bretts dieser Kantenlänge — die eine Quelle für beide Seiten. */
export function boardViewOf(sideLength: number, size = REFERENCE_HEX_SIZE): ViewBox {
  return boardViewBox(boardCells(createBoard({ sideLength })), size, size * BOARD_PADDING_RATIO);
}

/**
 * Wie weit das Brett vergrößert ist und worauf es zeigt — §64 („Zoom/Pan für kleinere
 * Bildschirme vorbereiten", umgesetzt in Zyklus D2d).
 *
 * **Der Maßstab ist ein Faktor, kein Pixelwert.** `1` heißt „das ganze Brett", `2` heißt „die
 * halbe Breite und Höhe davon". Damit hängt der Zoom nicht an der Fenstergröße — dasselbe `2`
 * zeigt auf dem Telefon und am Schreibtisch denselben Ausschnitt, nur unterschiedlich groß.
 *
 * `cx`/`cy` ist die Mitte des Ausschnitts in **Brettkoordinaten**, nicht in Bildschirmpunkten.
 * Die Umrechnung passiert an genau einer Stelle (Web-README, Entscheidung 6), und der Zoom darf
 * sie nicht zu einer zweiten machen.
 */
export interface BoardZoom {
  readonly scale: number;
  readonly cx: number;
  readonly cy: number;
}

export const NO_ZOOM: BoardZoom = { scale: 1, cx: 0, cy: 0 };

/** Grenzen des Maßstabs. Weiter als sechsfach wird ein Feld größer als der Bildschirm. */
export const ZOOM_LIMITS = { min: 1, max: 6 } as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Der sichtbare Ausschnitt bei einem gegebenen Maßstab.
 *
 * **Der Ausschnitt bleibt im Brett.** Ohne diese Klemmung ließe sich das Brett aus dem Bild
 * schieben, und wer das tut, sieht eine leere Fläche ohne Hinweis, wie er zurückkommt. Geklemmt
 * wird die **Mitte**, nicht der Rand: so bleibt die Bewegung stetig, statt an der Grenze zu
 * springen.
 *
 * Bei `scale <= 1` ist das Ergebnis exakt der Ausgangsausschnitt — der Normalfall kostet damit
 * keine Rechnung und keine Abweichung.
 */
export function zoomedViewBox(base: ViewBox, zoom: BoardZoom): ViewBox {
  const scale = clamp(zoom.scale, ZOOM_LIMITS.min, ZOOM_LIMITS.max);
  if (scale <= 1) return base;

  const width = base.width / scale;
  const height = base.height / scale;
  const cx = clamp(zoom.cx, base.minX + width / 2, base.minX + base.width - width / 2);
  const cy = clamp(zoom.cy, base.minY + height / 2, base.minY + base.height - height / 2);

  return { minX: cx - width / 2, minY: cy - height / 2, width, height };
}

/**
 * Ein Zoomschritt, der die Mitte behält — und beim Herauszoomen von selbst zurückfindet.
 *
 * Beim Verkleinern wandert die Mitte gegen die Brettmitte, weil `zoomedViewBox` sie klemmt; bei
 * `scale = 1` steht wieder das ganze Brett da. Dadurch braucht es keinen zweiten Weg zurück.
 *
 * **Der erste Schritt beginnt in der Brettmitte.** `NO_ZOOM` trägt `cx: 0, cy: 0`, und das ist
 * der Ursprung — bei einem Brett, das nicht darauf liegt (das echte beginnt bei etwa −690/−605),
 * also die obere linke Ecke. Ohne diese Unterscheidung sprang der erste Zoomschritt dorthin.
 * Aufgefallen ist das erst, als das Testbrett außermittig gelegt wurde; mit einem mittigen Brett
 * sind „Nullpunkt" und „Brettmitte" dasselbe, und der Fehler unsichtbar.
 */
export function withScale(base: ViewBox, zoom: BoardZoom, scale: number): BoardZoom {
  const next = clamp(scale, ZOOM_LIMITS.min, ZOOM_LIMITS.max);
  if (next <= 1) return NO_ZOOM;
  const von: BoardZoom =
    zoom.scale <= 1
      ? { scale: next, cx: base.minX + base.width / 2, cy: base.minY + base.height / 2 }
      : { ...zoom, scale: next };
  const box = zoomedViewBox(base, von);
  return { scale: next, cx: box.minX + box.width / 2, cy: box.minY + box.height / 2 };
}

/**
 * Verschieben um eine Strecke in **Brettkoordinaten**.
 *
 * Der Aufrufer rechnet Zeigerbewegung in Brettkoordinaten um — er kennt die Größe des Bildes,
 * diese Schicht nicht. Bei `scale = 1` passiert nichts: ein ganzes Brett lässt sich nicht
 * verschieben, und ein stummer Versatz wäre ein Zustand, den niemand sieht und niemand löst.
 */
export function panned(base: ViewBox, zoom: BoardZoom, dx: number, dy: number): BoardZoom {
  if (zoom.scale <= 1) return NO_ZOOM;
  const box = zoomedViewBox(base, { ...zoom, cx: zoom.cx + dx, cy: zoom.cy + dy });
  return { scale: zoom.scale, cx: box.minX + box.width / 2, cy: box.minY + box.height / 2 };
}
